import { isDefined } from "@clipboard-health/util-ts";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { type ReactNode } from "react";

import { Text } from "../../components/Text";

interface ChatMessageCardProps {
  title: string;
  subtitle?: string;
  caption?: string;
  statusChip?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

export function ChatMessageCard(props: ChatMessageCardProps) {
  const { title, subtitle, caption, statusChip, actions, children, sx } = props;

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          justifyContent: "center",
          padding: 2,
        },
        sx
      )}
    >
      <Card
        elevation={0}
        sx={(theme) => ({
          border: "1px solid",
          borderColor: theme.palette.divider,
          maxWidth: "400px",
          width: "100%",
          borderRadius: theme.borderRadius?.large,
        })}
      >
        <CardContent sx={actions ? { paddingBottom: 2 } : undefined}>
          <Stack direction="row" alignItems="flex-start" gap={2}>
            <Stack sx={{ marginRight: "auto", flex: 1 }}>
              <Text
                semibold
                variant="body2"
                sx={(theme) => ({ color: theme.palette.text.primary })}
              >
                {title}
              </Text>

              {isDefined(subtitle) && (
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  {subtitle}
                </Text>
              )}

              {isDefined(caption) && (
                <Text
                  variant="caption"
                  sx={(theme) => ({ color: theme.palette.text.secondary, marginTop: 0.5 })}
                >
                  {caption}
                </Text>
              )}

              {children}
            </Stack>

            {statusChip}
          </Stack>
        </CardContent>

        {actions && (
          <CardActions
            sx={{ paddingX: 2, paddingTop: 0, paddingBottom: 3, justifyContent: "center" }}
          >
            {actions}
          </CardActions>
        )}
      </Card>
    </Box>
  );
}
